import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {
  Button,
  IconLogo,
  Link,
  focusTrap,
} from '@resy/ui-kitchen';
import { redirectToSanitizedUrl } from '../../../../../../ui-kitchen/src/utils/sanitizeTargetUrl';
import { handleMktAnalytics } from '../../AmexMarketingUtils/AmexUtils';
import { CTA_EVENT_NAME } from '../../AmexMarketingUtils/constants';
import HeaderNav from './HeaderNav';
import MobileMenu from './MobileMenu';
import SideDrawer from './SideDrawer';
import './AmexGDAB2BHeader.scss';

const AmexGDAB2BHeader = (
  {
    $location,
    $rootScope,
    matchMedia,
    baseRequestUrl,
    contentData,
    Analytics,
    displayMenuItems,
  }
) => {
  const {
    link_bar: {
      links: linkBarLinks,
      resy_logo: {
        value: resyLogoLink = '',
      },
    },
    header: {
      menu_items: menuItems,
      logo,
      logo_aria: logoAria,
      mobile_footer_logo_headline: mobileFooterLogoHeadline,
      mobile_footer_logo_aria: mobileFooterLogoAria,
      mobile_footer_logo: mobileFooterLogo,
    },
  } = contentData;

  const [isScrolled, setIsScrolled] = useState(false);
  const [sideDrawerOpen, setIsSideDrawerOpen] = useState(false);
  const hasMenuItems = menuItems && menuItems.length > 0;

  const onLogoClickResyLogo = () => {
    const extraParam = {
      action_name: 'Header - Click',
      item_name: 'Resy logo',
    };
    handleMktAnalytics(Analytics.event, CTA_EVENT_NAME, extraParam);
    $location.path('/');
    $rootScope.$digest();
  };

  const handleHeaderLinkClick = (url, label) => {
    const extraParam = {
      action_name: 'Header - Click',
      item_name: label,
    };
    handleMktAnalytics(Analytics.event, CTA_EVENT_NAME, extraParam);
    redirectToSanitizedUrl(url);
  };

  const handleScroll = () => {
    if (window.scrollY > 60) {
      setIsScrolled(true);
    } else {
      setIsScrolled(false);
    }
  };

  const displayLinks = (link) => {
    const {
      label, url, name,
    } = link;

    switch (name) {
      case 'exploreResy':
        return (
          <Link
            key={label}
            className="explore-link"
            onClick={() => {
              handleHeaderLinkClick(url, label);
            }}
          >
            {label}
          </Link>
        );
      case 'exploreResyOS':
        return (
          <Link
            key={label}
            className="resy-link"
            target="_self"
            onClick={() => {
              handleHeaderLinkClick(url, label);
            }}
          >
            {label}
          </Link>
        );
      case 'gda':
        return (
          <Link
            key={label}
            className="gda-link"
            onClick={() => {
              handleHeaderLinkClick(url, label);
            }}
          >
            {label}
          </Link>
        );
      default:
        return null;
    }
  };

  const drawerClickHandler = () => {
    setIsSideDrawerOpen(!sideDrawerOpen);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const renderResyBar = () => (
    <nav
      className="AmexGDAB2BHeader__resybar"
      aria-label="Resy GDA B2B top menu"
    >
      <div className="container">
        <div className="AmexGDAB2BHeader__resybar__home">
          {resyLogoLink && (
            <Button
              className="AmexGDAB2BHeader__resy-logo"
              variant="no-style"
              aria-label="Go to Resy home."
              onClick={() => { onLogoClickResyLogo(); }}
            >
              <IconLogo height="25" width="68" />
            </Button>
          )}
          <div className="AmexGDAB2BHeader__resybar__links">
            {linkBarLinks && linkBarLinks.map((link) => displayLinks(link))}
          </div>
        </div>
      </div>
    </nav>
  );

  const renderMobileMenu = () => (
    <>
      <MobileMenu
        drawerClickHandler={() => drawerClickHandler()}
        resyLogoLink={resyLogoLink}
        show={sideDrawerOpen}
      />
      <SideDrawer
        onAfterClose={focusTrap.focusRemove}
        onAfterOpen={focusTrap.focusModal}
        menuItems={menuItems}
        mobileMenuContent={linkBarLinks}
        show={sideDrawerOpen}
        drawerClickHandler={() => drawerClickHandler()}
        logo={logo}
        logoAria={logoAria}
        baseRequestUrl={baseRequestUrl}
        mobileFooterLogo={mobileFooterLogo}
        mobileFooterLogoAria={mobileFooterLogoAria}
        mobileFooterLogoHeadline={mobileFooterLogoHeadline}
        displayMenuItems={displayMenuItems}
        Analytics={Analytics}
      />
    </>
  );

  const renderStickyBar = () => (
    <div
      className={cx('HeaderNav', {
        scrolled: isScrolled,
      })}
    >
      <HeaderNav
        baseRequestUrl={baseRequestUrl}
        menuItems={menuItems}
        logo={logo}
        logoAria={logoAria}
        displayMenuItems={displayMenuItems}
        Analytics={Analytics}
      />
    </div>
  );

  return (
    <>
      {matchMedia.lg && renderResyBar()}
      {!matchMedia.lg && (renderMobileMenu())}
      {(matchMedia.lg && hasMenuItems) && renderStickyBar()}
    </>
  );
};

AmexGDAB2BHeader.propTypes = {
  $location: PropTypes.shape({
    path: PropTypes.func,
  }).isRequired,
  $rootScope: PropTypes.shape({
    $digest: PropTypes.func,
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  baseRequestUrl: PropTypes.string.isRequired,
  contentData: PropTypes.shape({
    link_bar: PropTypes.shape({
      links: PropTypes.arrayOf(PropTypes.shape({
        explore_resy: PropTypes.shape({
          label: PropTypes.string,
          url: PropTypes.string,
          name: PropTypes.string,
          event_tag: PropTypes.string,
        }),
      })),
      resy_logo: PropTypes.shape({
        value: PropTypes.string,
        event_tag: PropTypes.string,
      }),
    }),
    logo_bar: PropTypes.shape({
      gda_link: PropTypes.string,
    }),
    header: PropTypes.shape({
      menu_items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
      logo: PropTypes.string.isRequired,
      logo_aria: PropTypes.string.isRequired,
      mobile_footer_logo_headline: PropTypes.string.isRequired,
      mobile_footer_logo_aria: PropTypes.string.isRequired,
      mobile_footer_logo: PropTypes.string.isRequired,
    }),
  }).isRequired,
  displayMenuItems: PropTypes.shape({}).isRequired,
};

export default AmexGDAB2BHeader;
